import { IReceipt } from '@/types/receipt'
import { QuestionnaireAnswerState, RootState } from '@/types/store'
import { QuestionnaireAnswerEntity } from '../../../api/@types'
import { AnswerSummary } from '../../types/questionnaire-answer'
import { GetterTree } from 'vuex'
import { QuestionType } from '@/types/questionnaire'

export interface QuestionnaireAnswerGetters
  extends GetterTree<QuestionnaireAnswerState, RootState> {
  questionnaireAnswer: (
    state: QuestionnaireAnswerState,
  ) => QuestionnaireAnswerEntity
  questionnaireAnswers: (
    state: QuestionnaireAnswerState,
  ) => QuestionnaireAnswerEntity[]
  isQuestionnaireAnswerLoading: (state: QuestionnaireAnswerState) => boolean
  receipt: (state: QuestionnaireAnswerState) => Partial<IReceipt> | null
  answerSummaries: (state: QuestionnaireAnswerState) => AnswerSummary[]
  nextAnswerId: (state: QuestionnaireAnswerState) => number | null
}

export const getters: QuestionnaireAnswerGetters = {
  questionnaireAnswer(state) {
    return state.questionnaireAnswer
  },
  questionnaireAnswers(state) {
    return state.questionnaireAnswers
  },
  isQuestionnaireAnswerLoading(state) {
    return state.isQuestionnaireAnswerLoading
  },
  receipt(state) {
    return state.receipt
  },
  answerSummaries(state) {
    const confirmationAnswers: AnswerSummary[] = []
    for (
      let qi = 0;
      qi < state.questionnaireAnswer.questionAnswers.length;
      qi++
    ) {
      const questionAnswer = state.questionnaireAnswer.questionAnswers[qi]
      switch (questionAnswer.type) {
        case QuestionType.TEXT:
          confirmationAnswers.push({
            questionAnswerId: questionAnswer.id,
            questionText: questionAnswer.question.questionText,
            type: QuestionType.TEXT,
            answerText: questionAnswer.text || '',
          })
          break

        case QuestionType.TEXT_AREA:
          confirmationAnswers.push({
            questionAnswerId: questionAnswer.id,
            questionText: questionAnswer.question.questionText,
            type: QuestionType.TEXT_AREA,
            answerText: questionAnswer.textArea || '',
          })
          break

        case QuestionType.DATE:
          confirmationAnswers.push({
            questionAnswerId: questionAnswer.id,
            questionText: questionAnswer.question.questionText,
            type: QuestionType.DATE,
            answerText: questionAnswer.date || '',
          })
          break

        case QuestionType.NUMBER:
          confirmationAnswers.push({
            questionAnswerId: questionAnswer.id,
            questionText: questionAnswer.question.questionText,
            type: QuestionType.NUMBER,
            answerText: String(questionAnswer.number),
          })
          break
        case QuestionType.FILE:
          // Todo: convert file to string
          confirmationAnswers.push({
            questionAnswerId: questionAnswer.id,
            questionText: questionAnswer.question.questionText,
            type: QuestionType.FILE,
            answerText: questionAnswer.text || '',
          })
          break
        case QuestionType.RADIO: {
          if (
            questionAnswer.choices.length !== 1 &&
            questionAnswer.question.choices
          ) {
            confirmationAnswers.push({
              questionAnswerId: questionAnswer.id,
              questionText: questionAnswer.question.questionText,
              type: QuestionType.RADIO,
              answerText: '未回答',
            })
            break
          }
          const questionChoice = (questionAnswer.question.choices || []).find(
            c => c.id === questionAnswer.choices[0].choiceId,
          )
          if (!questionChoice) {
            confirmationAnswers.push({
              questionAnswerId: questionAnswer.id,
              questionText: questionAnswer.question.questionText,
              type: QuestionType.RADIO,
              answerText: '未回答',
            })
            break
          }
          confirmationAnswers.push({
            questionAnswerId: questionAnswer.id,
            questionText: questionAnswer.question.questionText,
            type: QuestionType.RADIO,
            answerText: questionChoice.name,
          })
          break
        }

        case QuestionType.CHECKBOX: {
          const checkRadio = questionAnswer.choices.map(choice => {
            const questionchoice = (questionAnswer.question.choices || []).find(
              c => c.id === choice.choiceId,
            )
            return questionchoice?.name || null
          })
          const answerArray = checkRadio.filter(ans => ans !== null)
          if (answerArray.length === 0) {
            confirmationAnswers.push({
              questionAnswerId: questionAnswer.id,
              questionText: questionAnswer.question.questionText,
              type: QuestionType.CHECKBOX,
              answerText: '未回答',
            })
            break
          }
          let answerText = ''
          for (let ai = 0; ai < answerArray.length; ai++) {
            if (ai === 0) {
              answerText = String(answerArray[ai])
            } else {
              answerText = answerText + '\n' + answerArray[ai]
            }
          }
          confirmationAnswers.push({
            questionAnswerId: questionAnswer.id,
            questionText: questionAnswer.question.questionText,
            type: QuestionType.CHECKBOX,
            answerText,
          })
          break
        }

        case QuestionType.CROSS_RADIO: {
          let answerText = ''
          for (
            let ci = 0;
            ci < (questionAnswer.question.choices || []).length;
            ci++
          ) {
            const choice = questionAnswer.question.choices[ci]
            const choiceAnswer = (questionAnswer.choices || []).find(
              cr => cr.choiceId === choice.id,
            )
            if (!choiceAnswer) {
              continue
            }
            const cross = (questionAnswer.question.crosses || []).find(
              ch => ch.id === choiceAnswer.crossId,
            )
            if (!cross) {
              // error
              continue
            }
            answerText =
              answerText === ''
                ? `${choice.name}: ${cross.name}`
                : answerText + `\n${choice.name}: ${cross.name}`
          }
          confirmationAnswers.push({
            questionAnswerId: questionAnswer.id,
            questionText: questionAnswer.question.questionText,
            type: QuestionType.CROSS_RADIO,
            answerText,
          })
          break
        }

        case QuestionType.CROSS_CHECKBOX: {
          let answerText = ''
          for (
            let ci = 0;
            ci < (questionAnswer.question.choices || []).length;
            ci++
          ) {
            const choice = questionAnswer.question.choices[ci]
            const choiceAnswers = questionAnswer.choices.filter(
              cr => cr.choiceId === choice.id,
            )
            if (choiceAnswers.length === 0) {
              continue
            }
            let crossesText = ''
            for (let cj = 0; cj < choiceAnswers.length; cj++) {
              const cross = (questionAnswer.question.crosses || []).find(
                ch => ch.id === choiceAnswers[cj].crossId,
              )
              if (!cross) {
                // error
                continue
              }
              crossesText =
                crossesText === ''
                  ? cross.name
                  : crossesText + `, ${cross.name}`
            }
            answerText =
              ci === 0
                ? `${choice.name}: ${crossesText}`
                : answerText + `\n${choice.name}: ${crossesText}`
          }
          confirmationAnswers.push({
            questionAnswerId: questionAnswer.id,
            questionText: questionAnswer.question.questionText,
            type: QuestionType.CROSS_CHECKBOX,
            answerText,
          })
          break
        }
      }
    }
    return confirmationAnswers
  },
  nextAnswerId(state) {
    const currentId = state.questionnaireAnswer.id
    const nextAnswerIndex =
      state.questionnaireAnswers.findIndex(answer => answer.id === currentId) +
      1
    if (nextAnswerIndex === -1) {
      return null
    }
    if (
      nextAnswerIndex === state.questionnaireAnswers.length ||
      nextAnswerIndex > state.questionnaireAnswers.length
    ) {
      return null
    }
    return state.questionnaireAnswers[nextAnswerIndex].id
  },
}
